import React, {useEffect, useMemo, useState} from 'react'
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Container,
	Divider,
	FormControl,
	InputLabel,
	List,
	ListItemButton,
	ListSubheader,
	MenuItem,
	Paper,
	Select,
	Tab,
	Tabs,
	TextField,
	Tooltip,
	Typography,
	useTheme,
} from '@mui/material'
import Grid                                         from '@mui/material/Unstable_Grid2'
import {Check, ContentCopy, History, Save}          from '@mui/icons-material'
import {doGet, doPut}                               from '../../util/do-fetch'
import {applyValues, findUnknownPlaceholders, splitPlaceholders} from '../../util/prompt-utils'
import ConfigVersionHistory                         from './ConfigVersionHistory'
import PromptSampleData                             from './PromptSampleData'

/*
 * The Prompt Editor (090).
 *
 * App Config edits any key as an untyped string, which is right for a batch size and wrong for a
 * 900-character system prompt: it says nothing about what the prompt does, which placeholders it
 * accepts, or which temperature travels with it. This screen is the prompt-shaped view of the same
 * rows -- the catalogue comes from /admin/prompts, and saving still goes through the App Config
 * endpoint, so every edit keeps the version history and attribution from 088.
 *
 * Preview mode exists to answer "what does the model actually receive?". Fill the variables in the
 * side pane, watch them land in the text, then copy the finished prompt into a sandbox and run it.
 */

// what "Fill from real data" can derive from a product + topic + question
const SAMPLE_FILLABLE = new Set([
	'product_name', 'prepkit_name', 'prompt_name', 'topic', 'topic_name', 'topic_path',
	'section_name', 'question_count', 'correct_count', 'answered_count', 'skipped_count',
	'percent_correct', 'accuracy_percent', 'percent_correct_attempted',
	'questions', 'existing_questions', 'correct_answer', 'user_selected_answer',
])

const formatWhen = (iso) => {
	if (!iso) return ''
	const date = new Date(iso)
	return isNaN(date) ? '' : date.toLocaleString()
}

const PromptEditor = () => {
	const theme = useTheme()

	const [prompts, setPrompts] = useState(null)
	const [personalities, setPersonalities] = useState([])
	const [selectedKey, setSelectedKey] = useState(null)
	const [tab, setTab] = useState('edit')
	const [error, setError] = useState(null)

	const [draft, setDraft] = useState('')
	const [temperatureDraft, setTemperatureDraft] = useState('')
	const [saving, setSaving] = useState(false)
	const [values, setValues] = useState({})
	const [copied, setCopied] = useState(null)
	const [historyKey, setHistoryKey] = useState(null)

	const selected = prompts && prompts.find(p => p.key === selectedKey)

	const fetchPrompts = async (keepSelection = true) => {
		try {
			const response = await doGet('/admin/prompts')
			if (!response.ok) throw new Error('Could not load the prompts.')

			const data = await response.json()
			setPrompts(data)
			if (!keepSelection || !selectedKey) setSelectedKey(data.length ? data[0].key : null)
		}
		catch (err) {
			setError(err.message || 'Could not load the prompts.')
		}
	}

	useEffect(() => {
		fetchPrompts(false)

		// personalities come from the admin route because it includes the prompt text, which the
		// public /mini/ais deliberately omits
		doGet('/admin/ai-personalities')
			.then(response => (response.ok ? response.json() : []))
			.then(setPersonalities)
			.catch(() => setPersonalities([]))
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	// a different prompt is a different draft, a different temperature and different values
	useEffect(() => {
		if (!selected) return
		setDraft(selected.value || '')
		setTemperatureDraft(selected.temperature == null ? '' : String(selected.temperature))
		setValues({})
		setTab('edit')
	}, [selectedKey]) // eslint-disable-line react-hooks/exhaustive-deps

	const variableNames = useMemo(
		() => (selected ? selected.variables.map(v => v.name) : []),
		[selected]
	)

	const segments = useMemo(() => splitPlaceholders(draft, variableNames), [draft, variableNames])

	/*
	 * Tokens that look like placeholders but are not declared. Nothing substitutes them, so the
	 * model receives a literal `{whatever}` mid-instruction -- worth saying loudly, because one was
	 * already live in the question-generation prompt when this screen was built.
	 */
	const unknownPlaceholders = useMemo(
		() => findUnknownPlaceholders(draft, variableNames),
		[draft, variableNames]
	)

	// a declared placeholder the current text does not use: still offered, since it can be added,
	// but shown differently so nobody wonders why the value they typed never appears
	const usedNames = useMemo(
		() => new Set(segments.filter(s => s.type === 'variable').map(s => s.name)),
		[segments]
	)

	const groups = useMemo(() => {
		if (!prompts) return []
		const order = []
		const byGroup = new Map()
		for (const prompt of prompts) {
			if (!byGroup.has(prompt.group)) {
				byGroup.set(prompt.group, [])
				order.push(prompt.group)
			}
			byGroup.get(prompt.group).push(prompt)
		}
		return order.map(name => ({name, prompts: byGroup.get(name)}))
	}, [prompts])

	const copy = async (text, id) => {
		try {
			await navigator.clipboard.writeText(text)
			setCopied(id)
			setTimeout(() => setCopied(current => (current === id ? null : current)), 2000)
		}
		catch {
			setError('Could not copy to the clipboard.')
		}
	}

	const saveValue = async (key, value) => {
		setSaving(true)
		setError(null)
		try {
			const response = await doPut(`/admin/app-config/${encodeURIComponent(key)}`, {
				body: JSON.stringify({value: value}),
			})
			if (!response.ok) throw new Error('Could not save.')
			await fetchPrompts()
		}
		catch (err) {
			setError(err.message || 'Could not save.')
		}
		finally {
			setSaving(false)
		}
	}

	const isDirty = selected && draft !== (selected.value || '')
	const isTemperatureDirty = selected
		&& selected.temperatureKey
		&& temperatureDraft !== (selected.temperature == null ? '' : String(selected.temperature))

	const filledPrompt = applyValues(draft, variableNames, values)
	const unfilled = variableNames.filter(name => !values[name])

	return (
		<Container maxWidth="xl" sx={{py: 3}}>
			<Typography variant="h5" sx={{fontWeight: 700, mb: 1}}>Prompts</Typography>
			<Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
				Every prompt the AI is given, with its placeholders and temperature. Changes are live
				immediately and are recorded in the config history.
			</Typography>

			{error && <Alert severity="error" sx={{mb: 2}} onClose={() => setError(null)}>{error}</Alert>}

			{!prompts && <Box display="flex" justifyContent="center" py={6}><CircularProgress/></Box>}

			{prompts && (
				<Grid container spacing={2}>
					<Grid xs={12} md={3}>
						<Paper variant="outlined" sx={{borderRadius: 2}}>
							<List dense disablePadding>
								{groups.map(group => (
									<React.Fragment key={group.name}>
										{/* the headings used to read as just another row; a prompt list is
										    scanned, so the groups have to separate at a glance */}
										<ListSubheader sx={{
											fontWeight: 800,
											fontSize: '.7rem',
											letterSpacing: '.09em',
											textTransform: 'uppercase',
											lineHeight: '34px',
											color: theme.palette.text.primary,
											backgroundColor: theme.palette.action.selected,
											borderTop: `1px solid ${theme.palette.divider}`,
											borderBottom: `1px solid ${theme.palette.divider}`,
										}}>
											{group.name}
										</ListSubheader>
										{group.prompts.map(prompt => (
											<ListItemButton
												key={prompt.key}
												selected={prompt.key === selectedKey}
												onClick={() => setSelectedKey(prompt.key)}
												sx={{flexDirection: 'column', alignItems: 'flex-start', py: 1}}
											>
												<Typography variant="body2" sx={{fontWeight: 600}}>
													{prompt.label}
												</Typography>
												<Typography variant="caption" color="text.secondary">
													{prompt.value == null ? 'not set' : `v${prompt.version || 1}`}
													{prompt.temperature != null && ` · temperature ${prompt.temperature}`}
												</Typography>
											</ListItemButton>
										))}
									</React.Fragment>
								))}
							</List>
						</Paper>
					</Grid>

					<Grid xs={12} md={9}>
						{selected && (
							<Paper variant="outlined" sx={{borderRadius: 2, p: 3}}>
								<Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={2} flexWrap="wrap">
									<Box>
										<Typography variant="h6" sx={{fontWeight: 700}}>{selected.label}</Typography>
										<Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
											{selected.description}
										</Typography>
										<Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
											<Chip size="small" label={selected.key} sx={{fontFamily: 'monospace'}}/>
											{selected.value != null && (
												<Typography variant="caption" color="text.secondary">
													v{selected.version || 1}
													{selected.updated_by_email && ` · ${selected.updated_by_email}`}
													{selected.updated_at && ` · ${formatWhen(selected.updated_at)}`}
												</Typography>
											)}
										</Box>
									</Box>

									<Box display="flex" alignItems="center" gap={1}>
										<Button
											variant="outlined"
											size="small"
											startIcon={<History/>}
											onClick={() => setHistoryKey(selected.key)}
											sx={{textTransform: 'none'}}
										>
											Change history
										</Button>
									</Box>
								</Box>

								{selected.value == null && (
									<Alert severity="warning" sx={{mt: 2}}>
										This prompt has no row in <code>config</code> in this environment, so the feature
										that uses it will fail. Apply <code>schema-changes/011-prompt-config-seed.sql</code>.
									</Alert>
								)}

								{unknownPlaceholders.length > 0 && (
									<Alert severity="warning" sx={{mt: 2}}>
										<strong>
											{unknownPlaceholders.map(name => `{${name}}`).join(', ')}
										</strong>
										{unknownPlaceholders.length === 1 ? ' is not a placeholder this prompt supports.' : ' are not placeholders this prompt supports.'}
										{' '}Nothing substitutes {unknownPlaceholders.length === 1 ? 'it' : 'them'}, so the model
										receives the text literally. Remove {unknownPlaceholders.length === 1 ? 'it' : 'them'} or
										use one of the placeholders below.
									</Alert>
								)}

								<Divider sx={{my: 2}}/>

								<Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{mb: 2}}>
									<Tab value="edit" label="Edit"/>
									<Tab value="preview" label="Preview"/>
								</Tabs>

								{/*
								  * The placeholder chips are the point of the row: click one and the exact
								  * token is on the clipboard, ready to paste into the text. Nobody should be
								  * retyping {{user_selected_answer}} from memory.
								  */}
								{selected.variables.length > 0 && (
									<Box sx={{mb: 2}}>
										<Typography variant="caption" color="text.secondary">
											Placeholders — click to copy
										</Typography>
										<Box display="flex" gap={1} flexWrap="wrap" sx={{mt: .5}}>
											{selected.variables.map(v => (
												<Tooltip key={v.name}
												         title={copied === v.name
													         ? 'Copied'
													         : `${v.description}${usedNames.has(v.name) ? '' : ' — not used in this prompt yet'}`}>
													<Chip
														size="small"
														variant={usedNames.has(v.name) ? 'filled' : 'outlined'}
														label={`{{${v.name}}}`}
														onClick={() => copy(`{{${v.name}}}`, v.name)}
														icon={copied === v.name ? <Check fontSize="small"/> : <ContentCopy fontSize="small"/>}
														sx={{
															fontFamily: 'monospace',
															backgroundColor: usedNames.has(v.name)
																? theme.palette.action.selected
																: 'transparent',
														}}
													/>
												</Tooltip>
											))}
										</Box>
									</Box>
								)}

								{tab === 'edit' && (
									<>
										{/* editable here and read-only in Preview: Preview is for seeing what
										    the model gets, and a field you can change there invites edits that
										    look like part of the preview rather than a live save */}
										{selected.temperatureKey && (
											<Box display="flex" alignItems="center" justifyContent="flex-end" gap={1} sx={{mb: 1.5}}>
												<TextField
													size="small"
													label="Temperature"
													value={temperatureDraft}
													onChange={(e) => setTemperatureDraft(e.target.value)}
													sx={{width: 130}}
													helperText={selected.temperatureKey}
													FormHelperTextProps={{sx: {fontFamily: 'monospace', fontSize: '.65rem', ml: 0}}}
												/>
												{isTemperatureDirty && (
													<Button size="small" variant="outlined" disabled={saving}
													        onClick={() => saveValue(selected.temperatureKey, temperatureDraft)}>
														Save
													</Button>
												)}
											</Box>
										)}
										<TextField
											multiline
											fullWidth
											minRows={14}
											value={draft}
											onChange={(e) => setDraft(e.target.value)}
											InputProps={{sx: {fontFamily: 'monospace', fontSize: '.85rem'}}}
										/>
										<Box display="flex" alignItems="center" gap={2} sx={{mt: 2}}>
											<Button
												variant="contained"
												startIcon={saving ? <CircularProgress size={14}/> : <Save/>}
												disabled={!isDirty || saving || selected.value == null}
												onClick={() => saveValue(selected.key, draft)}
											>
												Save prompt
											</Button>
											{isDirty && (
												<Button size="small" onClick={() => setDraft(selected.value || '')}>
													Discard changes
												</Button>
											)}
											<Typography variant="caption" color="text.secondary">
												{draft.length} characters
											</Typography>
										</Box>
									</>
								)}

								{tab === 'preview' && (
									<Grid container spacing={2}>
										{selected.temperatureKey && (
											<Grid xs={12}>
												<Box display="flex" justifyContent="flex-end">
													<Chip
														size="small"
														variant="outlined"
														label={`Temperature ${selected.temperature == null ? '—' : selected.temperature}`}
													/>
												</Box>
											</Grid>
										)}
										<Grid xs={12} md={7}>
											<Box sx={{
												border: `1px solid ${theme.palette.divider}`,
												borderRadius: 1,
												p: 2,
												maxHeight: '55vh',
												overflow: 'auto',
												fontFamily: 'monospace',
												fontSize: '.85rem',
												whiteSpace: 'pre-wrap',
												wordBreak: 'break-word',
											}}>
												{segments.map((segment, i) => {
													if (segment.type === 'text') return <span key={i}>{segment.value}</span>

													const value = values[segment.name]
													// filled reads as part of the prompt, just traceable; unfilled
													// stays obviously a placeholder so it cannot be mistaken for text
													/*
													  * Substituted text has to be findable at a glance in 900
													  * characters of prompt, so these are solid blocks rather
													  * than a tint: green for filled, amber for still a
													  * placeholder, with a matching outline so they hold up on
													  * either theme.
													  */
													return (
														<Tooltip key={i} title={segment.name}>
															<Box component="span" sx={{
																backgroundColor: value
																	? (theme.palette.mode === 'dark' ? 'rgba(16,185,129,0.45)' : 'rgba(16,185,129,0.40)')
																	: (theme.palette.mode === 'dark' ? 'rgba(245,158,11,0.50)' : 'rgba(245,158,11,0.45)'),
																border: `1px solid ${value
																	? (theme.palette.mode === 'dark' ? 'rgba(16,185,129,0.9)' : 'rgba(5,150,105,0.7)')
																	: (theme.palette.mode === 'dark' ? 'rgba(245,158,11,0.9)' : 'rgba(217,119,6,0.7)')}`,
																borderRadius: '4px',
																fontWeight: 600,
																px: .5,
																py: '1px',
															}}>
																{value || segment.raw}
															</Box>
														</Tooltip>
													)
												})}
											</Box>

											<Box display="flex" alignItems="center" gap={2} sx={{mt: 2}}>
												<Button
													variant="contained"
													startIcon={copied === '__prompt__' ? <Check/> : <ContentCopy/>}
													onClick={() => copy(filledPrompt, '__prompt__')}
												>
													{copied === '__prompt__' ? 'Copied' : 'Copy prompt'}
												</Button>
												<Typography variant="caption" color="text.secondary">
													{unfilled.length === 0
														? 'All placeholders filled.'
														: `${unfilled.length} placeholder${unfilled.length === 1 ? '' : 's'} still unfilled — they are copied as-is.`}
												</Typography>
											</Box>
										</Grid>

										<Grid xs={12} md={5}>
											<Typography variant="subtitle2" sx={{mb: 1}}>Values</Typography>

											{/*
											  * Only offered where it can do something: a prompt whose
											  * placeholders are none of the ones derived from a product,
											  * topic and question would get a picker that fills nothing.
											  */}
											{selected.variables.some(v => SAMPLE_FILLABLE.has(v.name)) && (
												<PromptSampleData onFill={(filled) => setValues(prev => ({...prev, ...filled}))}/>
											)}
											{selected.variables.length === 0 && (
												<Typography variant="body2" color="text.secondary">
													This prompt takes no placeholders.
												</Typography>
											)}

											{selected.variables.map(v => (
												<Box key={v.name} sx={{mb: 2}}>
													{/*
													  * A personality is a whole prompt of its own, held in the
													  * ai_personality table -- picking one substitutes its text,
													  * exactly as the API does at request time.
													  */}
													{v.source === 'ai_personality' ? (
														<FormControl fullWidth size="small">
															<InputLabel>{v.name}</InputLabel>
															<Select
																label={v.name}
																value={values[v.name] || ''}
																onChange={(e) => setValues({...values, [v.name]: e.target.value})}
															>
																<MenuItem value=""><em>none</em></MenuItem>
																{personalities.map(p => (
																	<MenuItem key={p.id} value={p.prompt || ''}>{p.name}</MenuItem>
																))}
															</Select>
														</FormControl>
													) : (
														<TextField
															fullWidth
															size="small"
															label={v.name}
															multiline={Boolean(v.example && v.example.includes('\n'))}
															value={values[v.name] || ''}
															placeholder={v.example || ''}
															onChange={(e) => setValues({...values, [v.name]: e.target.value})}
															InputProps={{sx: {fontFamily: 'monospace', fontSize: '.8rem'}}}
														/>
													)}
													<Typography variant="caption" color="text.secondary">
														{v.description}
													</Typography>
												</Box>
											))}

											{selected.variables.length > 0 && (
												<Button size="small" onClick={() => setValues({})}>Clear values</Button>
											)}
										</Grid>
									</Grid>
								)}
							</Paper>
						)}
					</Grid>
				</Grid>
			)}

			<ConfigVersionHistory configKey={historyKey}
			                     open={Boolean(historyKey)}
			                     onClose={() => setHistoryKey(null)}
			/>
		</Container>
	)
}

export default PromptEditor
